import React, { lazy } from 'react';

import { RouteConfig, Roles } from '../../routing/child-router-factory';

import { pathJoin, CreateConfigFn, Role } from '../../routing/utils';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tickets = lazy(() => import('./pages/Tickets'));
const TicketDetail = lazy(() => import('./pages/TicketDetail'));
const Properties = lazy(() => import('./pages/Properties'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const Messages = lazy(() => import('./pages/Messages'));

type Keys =
  | `dashboard`
  | `messages`
  | `tickets`
  | `tickets/:propertyId/:ticketId`
  | `properties`
  | `properties/:propertyId`;

export const createRouteMap = (prefix: string) => ({
  dashboard: {
    _: pathJoin(prefix)(`dashboard`).value,
  },
  properties: {
    _: pathJoin(prefix)(`properties`).value,
    detail: (propertyId = ':propertyId') =>
      pathJoin(prefix)('properties')(propertyId).chain,
  },
  tickets: {
    _: pathJoin(prefix)(`tickets`).value,
    detail: (propertyId = ':propertyId', ticketId = ':ticketId') =>
      pathJoin(prefix)('tickets')(propertyId)(ticketId).chain,
  },
  messages: {
    _: pathJoin(prefix)(`messages`).value,
  },
});

export type RouteMap = ReturnType<typeof createRouteMap>;

const haulerUser: Roles = {
  and: ['authenticated', 'hauler'],
};

export const createConfig: CreateConfigFn<Keys> = (map) => {
  const arr: (Omit<RouteConfig<Keys>, 'fallback' | `roles`> & {
    roles?: Roles;
  })[] = [
    {
      key: 'dashboard',
      path: map.hauler.dashboard._,
      children: <Dashboard />,
    },
    // Detail before list so the more-specific `properties/:propertyId` route
    // is registered first; v7 route ranking would pick it anyway, but explicit
    // ordering avoids surprises.
    {
      key: 'properties/:propertyId',
      path: map.hauler.properties.detail()._,
      children: <PropertyDetail />,
    },
    {
      key: 'properties',
      path: map.hauler.properties._,
      children: <Properties />,
    },
    // Detail before list, same reason as properties above.
    {
      key: 'tickets/:propertyId/:ticketId',
      path: map.hauler.tickets.detail()._,
      children: <TicketDetail />,
    },
    {
      key: 'tickets',
      path: map.hauler.tickets._,
      children: <Tickets />,
    },
    {
      key: 'messages',
      path: map.hauler.messages._,
      children: <Messages />,
    },
  ];

  return [
    ...arr.map((c) => ({
      roles: haulerUser,
      // Unauthenticated → login. Authenticated but lacking the hauler role
      // (e.g. a citizen who hit /hauler) → bounce to citizen home rather than
      // returning '' (which renders blank).
      fallback: (roles: Role[]) => {
        if (roles.indexOf('authenticated') === -1) return map.login._;
        return map.app.selfService._;
      },
      ...c,
    })),
  ];
};
